// httpOnly cookie sessions.
// Stage 1: every auth token the app stores is also handed to the server once, which
// keeps it in an httpOnly cookie (unreadable by JS). Removing a token clears its cookie.
// Stage 2 (server flag COOKIE_ONLY_AUTH): once the cookie is confirmed, the stored token
// is replaced by a stand-in "header.payload.__cookie__" — same readable claims, no
// signature — which the server swaps for the cookie. No cookie => real token is kept.
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SYNCED_KEY = "invyt_cookie_synced";

// Only Invyt's own API gets cookies + the CSRF header (third-party uploads must not).
const isInvytApi = (url) => typeof url === "string" && (url.startsWith(API) || url.startsWith("/api/"));
axios.interceptors.request.use((config) => {
  if (isInvytApi(config.url)) {
    config.withCredentials = true;
    config.headers = config.headers || {};
    config.headers["X-Invyt-CSRF"] = "1";
  }
  return config;
});

const classify = (key) => {
  if (key === "attendee_token") return { kind: "attendee" };
  if (key === "organizer_token" || key === "invyt_organizer_token") return { kind: "organizer" };
  if (key === "superAdminToken") return { kind: "super_admin" };
  const m = /^venueAdminToken_(.+)$/.exec(key || "");
  if (m) return { kind: "venue_admin", slug: m[1] };
  if (key === "venue_admin_token") return { kind: "venue_admin" };
  return null;
};

const synced = () => {
  try { return new Set(JSON.parse(sessionStorage.getItem(SYNCED_KEY) || "[]")); } catch (_) { return new Set(); }
};
const markSynced = (sig) => {
  const s = synced(); s.add(sig);
  try { origSet.call(sessionStorage, SYNCED_KEY, JSON.stringify([...s].slice(-30))); } catch (_) { /* storage full/blocked */ }
};

const STAND_IN = ".__cookie__";
let cookieOnly = null; // null until /auth/config answers
const configReady = axios.get(`${API}/auth/config`)
  .then((r) => { cookieOnly = !!r.data?.cookie_only; })
  .catch(() => { cookieOnly = false; });

const standInFor = (token) => token.split(".").slice(0, 2).join(".") + STAND_IN;

const swapForStandIn = (key, token) => {
  if (!cookieOnly) return;
  const standIn = standInFor(token);
  [localStorage, sessionStorage].forEach((store) => {
    try { if (origGet.call(store, key) === token) origSet.call(store, key, standIn); } catch (_) { /* blocked storage */ }
  });
};

const sync = (key, kind, token) => {
  if (!token || token.split(".").length !== 3 || token.endsWith(STAND_IN)) return;
  const sig = `${kind}:${token.slice(-16)}`;
  if (synced().has(sig)) { // cookie already set this session
    configReady.then(() => swapForStandIn(key, token));
    return;
  }
  axios.post(`${API}/auth/session`, { kind, token })
    .then(async (r) => {
      // Only trust it once the browser really sends the cookie back (privacy modes may drop it).
      const check = await axios.get(`${API}/auth/session/check`);
      if (!(check.data?.cookies || []).includes(r.data?.cookie)) return;
      markSynced(sig);
      await configReady;
      swapForStandIn(key, token);
    })
    .catch(() => { /* invalid/expired token or cookies blocked: keep the real token */ });
};

const clear = (info) => {
  const params = { kind: info.kind, ...(info.slug ? { slug: info.slug } : {}) };
  if (info.kind === "venue_admin" && !info.slug) return; // generic key: per-venue cookie is cleared via its own key
  axios.delete(`${API}/auth/session`, { params }).catch(() => {});
};

const origSet = Storage.prototype.setItem;
const origGet = Storage.prototype.getItem;
const origRemove = Storage.prototype.removeItem;

export function installAuthCookieSync() {
  if (Storage.prototype.setItem.__invytPatched) return;
  const patchedSet = function (key, value) {
    origSet.call(this, key, value);
    const info = classify(key);
    if (info) sync(key, info.kind, value);
  };
  patchedSet.__invytPatched = true;
  Storage.prototype.setItem = patchedSet;
  Storage.prototype.removeItem = function (key) {
    const had = this.getItem(key) != null;
    origRemove.call(this, key);
    const info = classify(key);
    if (info && had && localStorage.getItem(key) == null && sessionStorage.getItem(key) == null) clear(info);
  };
  // Mirror sessions that existed before this release (keeps everyone signed in for Stage 2).
  [localStorage, sessionStorage].forEach((store) => {
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      const info = classify(key);
      if (info) sync(key, info.kind, store.getItem(key));
    }
  });
}
